// src/components/SmartFlow/nodes/registry/nodeRegistry.js

import { api } from '../../../../services/api';

/**
 * Frontend Node Registry
 *
 * Manages node metadata and integrates with backend API.
 * This registry acts as a cache and factory for node components.
 */
class NodeRegistry {
  constructor() {
    this.nodes = new Map();
    this.components = new Map();
    this.configComponents = new Map();
    this.initialized = false;
    this.loading = false;
    this.error = null;
  }

  /**
   * Register a node component (for custom/local nodes)
   * @param {string} nodeType - Node type identifier (e.g., 'HTTP_REQUEST')
   * @param {React.Component} component - Node React component
   * @param {React.Component} configComponent - Configuration React component
   */
  registerComponent(nodeType, component, configComponent = null) {
    console.log(`[NodeRegistry] Registering component: ${nodeType}`, component);
    this.components.set(nodeType, component);
    if (configComponent) {
      this.configComponents.set(nodeType, configComponent);
      console.log(`[NodeRegistry] Registered config component for: ${nodeType}`);
    }
  }

  /**
   * Initialize registry by fetching node metadata from backend
   * @returns {Promise<void>}
   */
  async initialize() {
    if (this.initialized || this.loading) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      const response = await api.get('/api/workflows/nodes');
      const nodesData = response.data;

      // Store metadata
      nodesData.forEach(nodeData => {
        this.nodes.set(nodeData.node_type, {
          type: nodeData.node_type,
          name: nodeData.display_name,
          icon: nodeData.icon,
          color: this._getCategoryColor(nodeData.category),
          category: nodeData.category,
          description: nodeData.description,
          configSchema: nodeData.config_schema || {},
          requiredIntegration: nodeData.required_integration,
        });
      });

      this.initialized = true;
      console.log(`[NodeRegistry] Initialized with ${this.nodes.size} nodes`);
    } catch (error) {
      this.error = error.message || 'Failed to load nodes';
      console.error('[NodeRegistry] Initialization failed:', error);
      throw error;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Get node metadata
   * @param {string} nodeType - Node type identifier
   * @returns {Object|null} Node metadata
   */
  get(nodeType) {
    return this.nodes.get(nodeType) || null;
  }

  /**
   * Get all node metadata
   * @returns {Array<Object>} Array of node metadata
   */
  getAll() {
    return Array.from(this.nodes.values());
  }

  /**
   * Get nodes by category
   * @param {string} category - Category name
   * @returns {Array<Object>} Filtered nodes
   */
  getByCategory(category) {
    return this.getAll().filter(node => node.category === category);
  }

  /**
   * Get node component
   * @param {string} nodeType - Node type identifier
   * @returns {React.Component|null} Node component
   */
  getComponent(nodeType) {
    return this.components.get(nodeType) || null;
  }

  /**
   * Get node configuration component
   * @param {string} nodeType - Node type identifier
   * @returns {React.Component|null} Config component
   */
  getConfigComponent(nodeType) {
    return this.configComponents.get(nodeType) || null;
  }

  /**
   * Get ReactFlow nodeTypes object (for WorkflowBuilder)
   * @returns {Object} nodeTypes mapping
   */
  getReactFlowNodeTypes() {
    const nodeTypes = {};

    // Return ALL registered components, not just those with metadata from API
    this.components.forEach((component, type) => {
      nodeTypes[type] = component;
    });

    console.log('[NodeRegistry] Returning nodeTypes:', Object.keys(nodeTypes));
    return nodeTypes;
  }

  /**
   * Check if registry is ready
   * @returns {boolean}
   */
  isReady() {
    return this.initialized && !this.loading && !this.error;
  }

  /**
   * Get category color (fallback if backend doesn't provide)
   * @private
   */
  _getCategoryColor(category) {
    const colorMap = {
      trigger: '#7B68EE',
      action: '#ff6d5a',
      condition: '#f39c12',
      utility: '#16a085',
      communication: '#3498db',
      data: '#9b59b6',
      logic: '#e67e22',
    };
    return colorMap[category] || '#7f8c8d';
  }

  /**
   * Clear registry (useful for testing)
   */
  clear() {
    this.nodes.clear();
    this.components.clear();
    this.configComponents.clear();
    this.initialized = false;
    this.loading = false;
    this.error = null;
  }
}

// Singleton instance
export const nodeRegistry = new NodeRegistry();

// Export class for testing
export { NodeRegistry };
